// Users: staff and customers in one place, with everything about signing in — invitations, reset links,
// temporary passwords and switching sign-in off.
import { api } from "../lib/api";
import { brand } from "../lib/brand";
import { fmt, t } from "../lib/i18n";
import { Link } from "../lib/router";
import { can, canAny, session } from "../lib/session";
import { h, invalidate, local, resource } from "../lib/ui";
import {
  act, Async, Button, Chip, confirmDialog, copyText, Dialog, Empty, Field, formData, isBusy, openDialog, showLinkDialog, toast,
} from "../shared/components";
import { Icon } from "../shared/icons";
import { PageHead } from "./app";
import { inviteStaffButton, StaffTab } from "./staff";

// ───────────── First sign-in for a new account ─────────────

/** "How will they sign in the first time?" — used when inviting staff and adding customers. */
export function DeliveryChoice(props: { id: string }) {
  const emailOn = !!brand.current.emailEnabled;
  const s = local(`screen:delivery.${props.id}`, () => ({ v: emailOn ? "email" : "temp_password" }));
  const options: [string, string, string][] = [
    ["email", t("admin.users.deliveryEmail"), emailOn ? t("admin.users.deliveryEmailHint") : t("admin.users.deliveryEmailOff")],
    ["temp_password", t("admin.users.deliveryTemp"), t("admin.users.deliveryTempHint")],
    ["link", t("admin.users.deliveryLink"), t("admin.users.deliveryLinkHint")],
  ];
  return (
    <fieldset class="stack tight" style="border:0;padding:0;margin:0">
      <legend class="label" style="margin-bottom:6px">{t("admin.users.delivery")}</legend>
      {options.map(([v, label, hint]) => {
        const off = v === "email" && !emailOn;
        return (
          <label class={`radio-card ${off ? "off" : ""}`}>
            <input type="radio" name="delivery" value={v} checked={s.v === v} disabled={off} onChange={() => s.set({ v })} />
            <span class="stack tight" style="gap:0"><span class="strong">{label}</span><span class="small muted">{hint}</span></span>
          </label>
        );
      })}
      {s.v === "temp_password" ? (
        <Field label={t("fields.tempPassword")} id={`${props.id}-tp`} hint={t("auth.passwordRule")}>
          <input class="input" id={`${props.id}-tp`} name="tempPassword" placeholder={t("admin.users.tempPlaceholder")} autocomplete="off" spellcheck={false} />
        </Field>
      ) : null}
    </fieldset>
  );
}

/** Tells the admin what happened after creating an account, and hands over the password or link if needed. */
export function showFirstSignIn(res: any, name: string, email: string, typedPassword?: string | null) {
  if (res.emailed) toast(t("admin.users.inviteSent", { email }));
  else if (res.tempPassword || typedPassword) showTempPassword(name, res.tempPassword ?? typedPassword, false);
  else if (res.inviteUrl) showLinkDialog(t("admin.staff.inviteLinkTitle"), res.inviteUrl, t("admin.users.linkBodyInvite", { name }));
  else toast(t("admin.homes.addedExisting", { name }));
}

export function showTempPassword(name: string, password: string, notified: boolean) {
  openDialog((close) => (
    <Dialog title={t("admin.users.tempResultTitle")} onClose={close}
      footer={[<Button kind="secondary" onClick={close}>{t("common.done")}</Button>, <Button icon="copy" onClick={() => copyText(password)}>{t("admin.users.copyPassword")}</Button>]}>
      <div class="stack">
        <div class="secret" dir="ltr">{password}</div>
        <p>{t("admin.users.tempResultBody", { name })}</p>
        {notified ? <p class="small muted">{t("admin.users.tempNotified", { name })}</p> : null}
      </div>
    </Dialog>
  ));
}

function tempPasswordDialog(u: any, done: () => void) {
  openDialog((close) => {
    const s = local(`screen:temp.${u.id}`, () => ({ mode: "generate" as "generate" | "own" }));
    return (
      <Dialog title={t("admin.users.tempTitle", { name: u.name })} onClose={close}>
        <form class="stack" onSubmit={(e: Event) => {
          const d = formData(e);
          act("user.temp", async () => {
            const res = await api.post(`/api/admin/users/${u.id}/temp-password`, s.mode === "own" ? { password: d.password ?? "" } : {});
            close();
            done();
            showTempPassword(u.name, res.password ?? d.password, !!u.has_password && !!brand.current.emailEnabled);
          });
        }}>
          <p>{t("admin.users.tempIntro", { name: u.name })}</p>
          <div class="seg" role="group">
            <button type="button" aria-pressed={String(s.mode === "generate")} onClick={() => s.set({ mode: "generate" })}>{t("admin.users.tempGenerate")}</button>
            <button type="button" aria-pressed={String(s.mode === "own")} onClick={() => s.set({ mode: "own" })}>{t("admin.users.tempOwn")}</button>
          </div>
          {s.mode === "own" ? (
            <Field label={t("fields.tempPassword")} id="tp-own" hint={t("auth.passwordRule")}>
              <input class="input" id="tp-own" name="password" required autocomplete="off" spellcheck={false} />
            </Field>
          ) : null}
          <div class="row end">
            <Button kind="secondary" onClick={close}>{t("common.cancel")}</Button>
            <Button type="submit" icon="key" busy={isBusy("user.temp")}>{t("admin.users.tempSet")}</Button>
          </div>
        </form>
      </Dialog>
    );
  });
}

/** Sign-in state and actions for one person, shown in their staff or customer dialog. */
export function SignInSection(props: { u: any; kind: "staff" | "customer"; reload: () => void }) {
  const u = props.u;
  const self = u.id === session.me?.id;
  const emailOn = !!brand.current.emailEnabled;
  // The server checks all of this again; these only decide which buttons to show.
  const strong = props.kind === "staff" ? can("admin.staff") : can("admin.users");
  const canEmail = props.kind === "staff" ? can("admin.staff") : canAny("ops.members", "admin.users");
  // Any link handed to staff lets whoever uses it into the account, so for customers it needs Sign-in and passwords.
  const canCopy = props.kind === "staff" ? strong : can("admin.users");
  const active = u.status !== "disabled";
  const state = !active ? t("userStatus.disabled")
    : !u.has_password ? t("admin.users.notSignedUp")
    : u.must_change_password && u.password_expires_at ? t("admin.users.tempUntil", { date: fmt.date(u.password_expires_at, "short") })
    : `${t("admin.users.lastLogin")}: ${u.last_login_at ? fmt.relative(u.last_login_at) : t("admin.users.never")}`;
  const purpose = u.has_password ? "reset" : "invite";
  return (
    <div class="signin-box">
      <div class="row between" style="gap:8px">
        <span class="strong row" style="gap:6px"><Icon name="lock" size={16} />{t("admin.users.signIn")}</span>
        <span class="small muted">{state}</span>
      </div>
      <div class="small"><span class="muted">{t("admin.users.username")}: </span><span class="strong" dir="ltr">{u.email}</span></div>
      {!self && active ? (
        <div class="actions">
          {canEmail && emailOn ? (
            <Button kind="secondary" size="small" icon="mail" busy={isBusy(`u.mail.${u.id}`)} onClick={() => act(`u.mail.${u.id}`, async () => {
              const res = await api.post(`/api/admin/users/${u.id}/send-email`);
              toast(res.purpose === "invite" ? t("admin.users.inviteSent", { email: res.to }) : t("admin.users.resetSent", { email: res.to }));
            })}>{purpose === "invite" ? t("admin.users.sendInvite") : t("admin.users.sendReset")}</Button>
          ) : null}
          {canCopy ? (
            <Button kind="ghost" size="small" icon="link" onClick={() => act(`u.link.${u.id}`, async () => {
              const res = await api.post(`/api/admin/users/${u.id}/link`);
              showLinkDialog(res.purpose === "invite" ? t("admin.staff.inviteLinkTitle") : t("admin.staff.resetLinkTitle"), res.url,
                res.purpose === "invite" ? t("admin.users.linkBodyInvite", { name: u.name }) : t("admin.users.linkBodyReset", { name: u.name }));
            })}>{purpose === "invite" ? t("admin.users.copyInvite") : t("admin.users.copyReset")}</Button>
          ) : null}
          {strong ? <Button kind="ghost" size="small" icon="key" onClick={() => tempPasswordDialog(u, props.reload)}>{t("admin.users.temp")}</Button> : null}
        </div>
      ) : null}
      {props.kind === "customer" && !self && can("admin.users") ? (
        <div class="actions">
          {active ? (
            <Button kind="danger-outline" size="small" icon="power" onClick={async () => {
              if (!(await confirmDialog({ title: t("admin.users.disableTitle", { name: u.name }), body: t("admin.users.disableBody", { name: u.name }), confirm: t("admin.users.disable"), danger: true }))) return;
              act(`u.status.${u.id}`, async () => {
                await api.post(`/api/admin/users/${u.id}/status`, { status: "disabled" });
                props.reload();
              }, t("admin.users.disabledToast"));
            }}>{t("admin.users.disable")}</Button>
          ) : (
            <Button kind="secondary" size="small" icon="power" onClick={() => act(`u.status.${u.id}`, async () => {
              await api.post(`/api/admin/users/${u.id}/status`, { status: "active" });
              props.reload();
            }, t("admin.users.enabledToast"))}>{t("admin.users.enable")}</Button>
          )}
        </div>
      ) : null}
      {props.kind === "customer" && !strong && canEmail ? <p class="xs muted" style="margin:0">{t("admin.users.noAccessNote")}</p> : null}
    </div>
  );
}

// ───────────── Customers ─────────────

function customerDialog(id: string) {
  openDialog((close) => {
    const list = resource("admin.customers.all", () => api.get("/api/admin/customers"));
    const u = (list.data ?? []).find((x: any) => x.id === id);
    if (!u) return <Dialog title="" onClose={close}><p class="muted">{t("common.loading")}</p></Dialog>;
    return (
      <Dialog title={u.name} onClose={close} footer={<Button kind="secondary" onClick={close}>{t("common.close")}</Button>}>
        <div class="stack">
          <div class="stack tight small">
            {u.phone ? <span><span class="muted">{t("fields.phone")}: </span>{u.phone}</span> : null}
            <span><span class="muted">{t("fields.locale")}: </span>{t(`languages.${u.locale}`)}</span>
          </div>
          <div class="stack tight">
            <span class="label">{t("admin.users.homes")}</span>
            {u.homes.length ? u.homes.map((hm: any) => (
              <div class="row between small" style="gap:8px">
                {can("ops.homes") ? <Link href={`/admin/homes/${hm.id}`}>{hm.code} · {hm.address}</Link> : <span>{hm.code} · {hm.address}</span>}
                <span class="muted">{hm.status === "revoked" ? t("memberStatus.revoked") : hm.roles.map((r: string) => t(`homeRole.${r}`)).join(", ")}</span>
              </div>
            )) : <span class="small muted">{t("common.none")}</span>}
          </div>
          <SignInSection u={u} kind="customer" reload={() => invalidate("admin.customers")} />
        </div>
      </Dialog>
    );
  });
}

function CustomersTab() {
  const s = local("screen:customers", () => ({ q: "" }));
  const all = resource("admin.customers.all", () => api.get("/api/admin/customers"));
  const q = s.q.trim().toLowerCase();
  return (
    <div class="stack">
      <div style="max-width:420px">
        <input class="input" type="search" placeholder={t("admin.users.search")} value={s.q}
          onInput={(e: Event) => s.set({ q: (e.target as HTMLInputElement).value })} aria-label={t("admin.users.search")} />
      </div>
      <section class="panel">
        {Async(all, (rows: any[]) => {
          const shown = q ? rows.filter((u) => `${u.name} ${u.email} ${u.phone ?? ""}`.toLowerCase().includes(q)) : rows;
          if (!rows.length) return <Empty title={t("admin.users.noCustomersYet")} />;
          if (!shown.length) return <Empty title={t("admin.users.noCustomers")} />;
          return (
            <div class="table-wrap">
              <table class="table">
                <thead><tr><th>{t("fields.name")}</th><th>{t("admin.users.homes")}</th><th>{t("fields.status")}</th><th>{t("admin.users.lastLogin")}</th></tr></thead>
                <tbody>
                  {shown.map((u) => (
                    <tr class="link" onClick={() => customerDialog(u.id)}>
                      <td><span class="strong">{u.name}</span><span class="sub">{u.email}</span></td>
                      <td class="small">{u.homes.filter((hm: any) => hm.status !== "revoked").map((hm: any) => (
                        <div>{hm.code} <span class="muted">· {hm.roles.map((r: string) => t(`homeRole.${r}`)).join(", ")}</span></div>
                      ))}</td>
                      <td>
                        <div class="row" style="gap:6px;flex-wrap:wrap">
                          <Chip kind={u.status === "active" ? "good" : u.status === "invited" ? "accent" : "neutral"}>{t(`userStatus.${u.status}`)}</Chip>
                          {u.must_change_password ? <Chip kind="attention">{t("admin.users.tempChip")}</Chip> : null}
                        </div>
                      </td>
                      <td class="small muted">{u.last_login_at ? fmt.relative(u.last_login_at) : t("admin.users.never")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }, 5)}
      </section>
    </div>
  );
}

// ───────────── Page ─────────────

export function UsersPage() {
  const tabs = [can("admin.staff") ? "staff" : null, canAny("ops.members", "admin.users", "ops.homes") ? "customers" : null].filter(Boolean) as string[];
  const s = local("screen:users", () => ({ tab: tabs[0] ?? "staff" }));
  const tab = tabs.includes(s.tab) ? s.tab : tabs[0];
  return (
    <div class="stack loose">
      <PageHead title={t("admin.users.title")} intro={t("admin.users.intro")} actions={tab === "staff" ? inviteStaffButton() : null} />
      {!brand.current.emailEnabled ? (
        <div class="note row between" style="gap:12px;flex-wrap:wrap">
          <span class="row" style="gap:8px;align-items:flex-start"><Icon name="mail" size={18} /><span>{t("admin.users.emailOff")}</span></span>
          {can("admin.settings") ? <Link href="/admin/email" class="btn secondary small">{t("admin.users.setUpEmail")}</Link> : null}
        </div>
      ) : null}
      {tabs.length > 1 ? (
        <div class="seg" role="tablist">
          {tabs.map((x) => <button role="tab" aria-pressed={String(tab === x)} onClick={() => s.set({ tab: x })}>{t(`admin.users.tabs.${x}`)}</button>)}
        </div>
      ) : null}
      {tab === "staff" ? <StaffTab /> : <CustomersTab />}
    </div>
  );
}
